import { useEffect, useState } from 'react';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Input, Select, Card, Badge, getStatusBadgeVariant, Pagination, Modal, ModalFooter } from '@/components/common';
import { useRenewalBatches, useCreateRenewalBatch } from '@/hooks/useRenewals';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useAuthStore } from '@/store/auth';
import { formatDate, titleCase } from '@/utils';
import type { BrokerContext, RenewalBatchFilters } from '@/types';

// RenewalBatch.STATUS_CHOICES verbatim (backend/apps/renewals/models.py). The
// old list offered "Active", which is not one of them: `filterset_fields =
// ["status"]` builds a ChoiceFilter from those choices, so picking it sent
// `?status=active` and the list came back 400/empty -- a filter that could
// only ever say "no batches". "Scheduled", "In Progress" and "Cancelled" were
// missing, so batches in those states could not be filtered for at all.
const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function BatchesPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<RenewalBatchFilters>({});
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedTerm` (300ms)
  // feeds `filters.search`, replacing the old submit-on-Enter form.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch();
  const [showCreateModal, setShowCreateModal] = useState(false);
  // These two keys ARE the serializer's field names, deliberately. Introspecting
  // RenewalBatchSerializer in this tree gives
  //   REQUIRED = ['broker', 'expiry_end_date', 'expiry_start_date', 'name']
  // and `end_date` is not a field on it at all, while `start_date` is the
  // OPTIONAL schedule date (models.py: null=True), NOT the expiry window that
  // `__str__` prints and the batch `generate` action selects policies with.
  // The old {start_date, end_date} payload therefore 400ed on two missing
  // required fields AND silently dropped the End Date the user typed.
  const [newBatch, setNewBatch] = useState({
    name: '',
    description: '',
    expiry_start_date: '',
    expiry_end_date: '',
  });

  const { data, isLoading, isError, refetch } = useRenewalBatches(page, filters);
  const createMutation = useCreateRenewalBatch();

  // RenewalBatchSerializer requires `broker` (apps/renewals/models.py: a
  // plain FK, not nullable) and this form never sent one -- every create was
  // a guaranteed 400 for the broker_admin/broker_employee users this page is
  // routed to. Same expression PolicyCreatePage and CommissionsListPage use:
  // the BrokerSwitcher's current selection first, since a broker-side user
  // can belong to more than one broker and `user.tenant_id` names only the
  // one the login token was issued for.
  const loginType = useAuthStore((s) => s.loginType);
  const tenantContext = useAuthStore((s) => s.tenantContext);
  const user = useAuthStore((s) => s.user);
  const brokerId =
    (loginType === 'broker' ? (tenantContext as BrokerContext | null)?.id ?? undefined : undefined) ??
    (user?.tenant_type === 'broker' ? user.tenant_id ?? undefined : undefined);

  const batches = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    const next = debouncedTerm || undefined;
    setFilters((prev) => (prev.search === next ? prev : { ...prev, search: next }));
    setPage(1);
  }, [debouncedTerm]);

  const handleCreate = () => {
    if (newBatch.name && newBatch.expiry_start_date && newBatch.expiry_end_date && brokerId) {
      createMutation.mutate(
        { ...newBatch, broker: brokerId } as Parameters<typeof createMutation.mutate>[0],
        {
          onSuccess: () => {
            setShowCreateModal(false);
            setNewBatch({
              name: '',
              description: '',
              expiry_start_date: '',
              expiry_end_date: '',
            });
          },
        }
      );
    }
  };

  return (
    <div>
      <PageHeader
        title="Renewal Batches"
        // `count` is 0 on a failed request too, and "0 batches" in the header
        // is the same false claim as the empty state below.
        subtitle={isError ? 'Could not load batches' : `${totalItems} batches`}
        breadcrumbs={[
          { label: 'Renewals', href: '/renewals' },
          { label: 'Batches' },
        ]}
        actions={
          <Button
            leftIcon={<PlusIcon className="w-5 h-5" />}
            onClick={() => setShowCreateModal(true)}
          >
            Create Batch
          </Button>
        }
      />

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search batches..."
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Select
            options={statusOptions}
            value={filters.status || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value || undefined }))}
            className="w-48"
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
              <div className="h-3 bg-gray-200 rounded w-20 mb-4" />
              <div className="h-20 bg-gray-100 rounded" />
            </Card>
          ))
        ) : isError ? (
          // A failed GET tells us nothing about whether this broker has
          // batches. Rendering the empty state here asserts that their
          // renewal book has none -- which is how someone builds a second
          // batch on top of the ones the request failed to show them.
          <div className="col-span-full text-center py-12">
            <h3 className="text-sm font-semibold text-red-700">Couldn&apos;t load renewal batches.</h3>
            <p className="mt-1 text-sm text-gray-500">
              That is not the same as there being none — please try again.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : batches.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <h3 className="text-sm font-semibold text-gray-900">No batches found</h3>
            <p className="mt-1 text-sm text-gray-500">Create your first renewal batch.</p>
          </div>
        ) : (
          batches.map((batch) => (
            // B4 item 7: every field drawn here is one the serializer actually
            // returns. `batch_number` and `pending_count` were not fields on
            // RenewalBatch at all, and `end_date` is not on the serializer, so
            // this card used to print an empty line under the name, an empty
            // date range and an empty "Pending" tile on every batch.
            // `Card` takes only children/className/padding, so the test hook
            // goes on a native wrapper rather than on the component.
            <Card key={batch.id}>
              <div data-testid={`batch-card-${batch.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{batch.name}</h3>
                  <p className="text-xs text-gray-500">
                    Created {formatDate(batch.created_at)}
                    {batch.created_by_name ? ` by ${batch.created_by_name}` : ''}
                  </p>
                </div>
                <Badge variant={getStatusBadgeVariant(batch.status)} size="sm">
                  {titleCase(batch.status)}
                </Badge>
              </div>
              {batch.description && (
                <p className="text-sm text-gray-600 mb-3">{batch.description}</p>
              )}
              {/* The batch's criteria window, which is what `expiry_start_date`
                  / `expiry_end_date` mean -- not a schedule for the batch. */}
              <div className="text-xs text-gray-500 mb-4">
                Policies expiring {formatDate(batch.expiry_start_date)} –{' '}
                {formatDate(batch.expiry_end_date)}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-50 p-2 rounded">
                  <p className="text-lg font-semibold text-gray-900">{batch.total_policies}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <p className="text-lg font-semibold text-green-600">{batch.renewed_count}</p>
                  <p className="text-xs text-green-600">Renewed</p>
                </div>
                {/* Counted by the server, unlike the old "Pending", which was a
                    field nothing set and so always blank. */}
                <div className="bg-red-50 p-2 rounded">
                  <p className="text-lg font-semibold text-red-600">{batch.lapsed_count}</p>
                  <p className="text-xs text-red-600">Lapsed</p>
                </div>
              </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setPage}
          />
        </div>
      )}

      {/* Create Batch Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Renewal Batch"
      >
        <div className="space-y-4">
          <Input
            label="Batch Name"
            value={newBatch.name}
            onChange={(e) => setNewBatch((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., January 2024 Renewals"
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={2}
              value={newBatch.description}
              onChange={(e) => setNewBatch((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Batch description..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {/* The batch's criteria window: which policies it sweeps up, by
                expiry date. Labelled for what it selects rather than as a bare
                "Start"/"End", which read like a schedule for the batch itself. */}
            <Input
              label="Policies Expiring From"
              type="date"
              value={newBatch.expiry_start_date}
              onChange={(e) =>
                setNewBatch((prev) => ({ ...prev, expiry_start_date: e.target.value }))
              }
              required
            />
            <Input
              label="Policies Expiring To"
              type="date"
              value={newBatch.expiry_end_date}
              onChange={(e) =>
                setNewBatch((prev) => ({ ...prev, expiry_end_date: e.target.value }))
              }
              required
            />
          </div>
          {!brokerId && (
            <p className="text-sm text-amber-700">
              Could not determine which broker this batch belongs to. Try again after switching
              broker, or reload the page.
            </p>
          )}
        </div>
        <ModalFooter>
          <Button variant="outline" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            isLoading={createMutation.isPending}
            disabled={
              !newBatch.name ||
              !newBatch.expiry_start_date ||
              !newBatch.expiry_end_date ||
              !brokerId
            }
          >
            Create Batch
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
